// One workshop: who works there, and everything that is edited per workshop.
//
// This replaces the old separate "Workshop" page — Workshop and Workshops
// were two views of the same thing, so the list now drills into this instead.

import { useState } from "react";
import { ChevronLeft, Pencil, Plus } from "lucide-react";
import { peopleAtWorkshop, personRemovalNote, snapshotRoleLabel, unfilledRoles } from "../../lib/organization";
import { useTenantTerms } from "../../lib/TenantConfigContext";
import { PrimaryButton, cardClass } from "./primitives";
import { ConfirmDelete, StatusChip } from "./OrgKit";
import PersonDialog from "./PersonDialog";
import { usePersonRemoval } from "./useOrgEditors";

export default function WorkshopDetail({ tenantId, ix, workshop, onBack }) {
  const location = workshop.workshop_location;
  // Super Admin renders outside TenantConfigProvider, where roleLabel falls
  // back to platform defaults — the brand's own labels are in the snapshot,
  // so prefer those and keep roleLabel only as the backstop.
  const { roleLabel: contextRoleLabel } = useTenantTerms();
  const roleLabel = (code) => snapshotRoleLabel(ix, code, contextRoleLabel);
  const { deleting, setDeleting, askRemove, confirmRemove, pending } = usePersonRemoval(tenantId, ix);
  const [addingPerson, setAddingPerson] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);

  const everyone = peopleAtWorkshop(location, ix);
  // Service advisors are listed with everyone else: same row, same edit and
  // remove, so there is one way to look after anyone at the workshop.
  const staff = everyone;
  const unfilled = unfilledRoles(location, ix);

  const dealer = workshop.dealer_id ? ix.dealerById.get(workshop.dealer_id) : null;

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-2.5 inline-flex items-center gap-1 text-[13px] font-medium text-[#6B7280] hover:text-[#111827]"
      >
        <ChevronLeft size={15} /> All workshops
      </button>

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="min-w-0">
          <div className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Workshop</div>
          <h1 className="mt-[6px] text-[28px] font-semibold leading-[36px] text-[#111827]">{location}</h1>
          <p className="mt-2 text-[14px] leading-[20px] text-[#4B5563]">
            {dealer ? dealer.name : "No dealer"} · {everyone.length}{" "}
            {everyone.length === 1 ? "person" : "people"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusChip status={workshop.status === "paused" ? "paused" : "active"} />
          <PrimaryButton onClick={() => { setEditingPerson(null); setAddingPerson(true); }}>
            <Plus size={16} /> Add person
          </PrimaryButton>
        </div>
      </div>

      <div className={`mt-7 overflow-hidden ${cardClass}`}>
        {staff.length ? (
          staff.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-4 px-5 py-3.5 shadow-[inset_0_-1px_0_#F0F1F3] last:shadow-none"
              data-testid="workshop-person"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-medium text-[#111827]">
                  {p.name || (p.email || "").split("@")[0]}
                </div>
                <div className="truncate text-[13px] text-[#6B7280]">
                  {p.email || <span className="text-[#9CA3AF]">No login</span>}
                </div>
              </div>
              <div className="shrink-0 text-[13px] text-[#4B5563]">{roleLabel(p.role)}</div>
              <div className="w-36 shrink-0 text-right text-[13px] text-[#6B7280]">
                {p.number || <span className="text-[#9CA3AF]">No number</span>}
              </div>
              <div className="w-20 shrink-0 whitespace-nowrap text-right">
                <button
                  type="button"
                  onClick={() => { setEditingPerson(p); setAddingPerson(true); }}
                  aria-label={`Edit ${p.name || p.email}`}
                  className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#111827]"
                >
                  <Pencil size={15} />
                </button>
                <button
                    type="button"
                    onClick={() => askRemove(p.id, p.has_login)}
                    aria-label={`Remove ${p.name || p.email}`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
                    </svg>
                  </button>
              </div>
            </div>
          ))
        ) : (
          <div className="px-5 py-6 text-[13px] text-[#6B7280]">Nobody is recorded at this workshop yet.</div>
        )}
      </div>

      {unfilled.length > 0 && (
        <p className="mt-3 text-[13px] text-[#6B7280]">
          Not staffed: {unfilled.map((k) => roleLabel(k)).join(", ")}
        </p>
      )}

      <PersonDialog
        open={addingPerson}
        onOpenChange={setAddingPerson}
        tenantId={tenantId}
        ix={ix}
        person={editingPerson}
        atWorkshop={editingPerson ? null : location}
      />

      <ConfirmDelete
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        name={deleting?.name || ""}
        body={personRemovalNote(deleting?.hasLogin)}
        onConfirm={confirmRemove}
        pending={pending}
      />
    </>
  );
}
